// @flow

import { useMemo } from 'react';
import { type VrUserInputValues, type VrUserInputValuesKey } from './user-input-values';
import { validateCpf as checkCpf } from '@tectonics/commons/cpf';
import { validateCardHolderName as checkCardHolderName } from '@tectonics/commons/card/holder-name';
import { validateCardNumber as checkCardNumber } from '@tectonics/commons/card/card-number';
import {
  validateCardDueDatePattern as checkCardDueDatePattern,
  validateCardDueDateExpiration as checkCardDueDateExpiration,
} from '@tectonics/commons/card/due-date';

export type VrUserInputValidations = {
  [VrUserInputValuesKey]: VrUserInputValidation,
};

type VrUserInputValidation = ?string;

export function useVrUserInputValidations(vrUserInputValues: VrUserInputValues): VrUserInputValidations {
  const userBrazilianCpf = useValidationMemo(vrUserInputValues, 'userBrazilianCpf');
  const cardHolderName = useValidationMemo(vrUserInputValues, 'cardHolderName');
  const cardNumber = useValidationMemo(vrUserInputValues, 'cardNumber');
  const cardDueDate = useValidationMemo(vrUserInputValues, 'cardDueDate');
  const cardCvv = useValidationMemo(vrUserInputValues, 'cardCvv');

  return useMemo(() => {
    return {
      userBrazilianCpf,
      cardHolderName,
      cardNumber,
      cardDueDate,
      cardCvv,
    };
  }, [userBrazilianCpf, cardHolderName, cardNumber, cardDueDate, cardCvv]);
}

const VALIDATORS: { [VrUserInputValuesKey]: (string) => ?string } = {
  userBrazilianCpf: validateUserBrazilianCpf,
  cardHolderName: validateCardHolderName,
  cardNumber: validateCardNumber,
  cardDueDate: validateCardDueDate,
  cardCvv: validateCardCvv,
};

function useValidationMemo(vrUserInputValues: VrUserInputValues, key: VrUserInputValuesKey): ?string {
  return useMemo(() => {
    return VALIDATORS[key](vrUserInputValues[key]);
  }, [vrUserInputValues[key]]);
}

function validateUserBrazilianCpf(userBrazilianCpf: string): ?string {
  if (!userBrazilianCpf) {
    return 'empty';
  }

  if (!checkCpf(userBrazilianCpf)) {
    return 'invalid';
  }
}

function validateCardHolderName(cardHolderName: string): ?string {
  if (!cardHolderName) {
    return 'empty';
  }

  if (!checkCardHolderName(cardHolderName)) {
    return 'invalid';
  }
}

function validateCardNumber(cardNumber: string): ?string {
  if (!cardNumber) {
    return 'empty';
  }

  if (!checkCardNumber(cardNumber)) {
    return 'invalid';
  }
}

function validateCardDueDate(cardDueDate: string): ?string {
  if (!cardDueDate) {
    return 'empty';
  }

  if (!checkCardDueDatePattern(cardDueDate)) {
    return 'invalid';
  }

  if (!checkCardDueDateExpiration(cardDueDate)) {
    return 'expired';
  }
}

function validateCardCvv(cardCvv: string): ?string {
  if (!cardCvv) {
    return 'empty';
  }
}
